import React, { useState } from "react";
import { showSuccess, showError } from "../../utils/sweetAlert";
import { authFetch, forgotPasswordAPI, loginAPI } from "../../utils/api";
import "./Login.css";

// Sign a non-admin token out on the server; it was never stored in the browser
const revoke = (token) =>
  authFetch("/logout", { method: "POST", headers: { Authorization: `Bearer ${token}` } }).catch(() => {});

function Login({ onLogin }) {
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [formError, setFormError] = useState("");

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    if (formError) setFormError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.email.trim() || !formData.password) {
      setFormError("Please enter both your email and password.");
      return;
    }

    setIsLoading(true);
    setFormError("");

    try {
      const data = await loginAPI(formData.email.trim(), formData.password);

      if (data && data.success && data.user?.role !== "admin") {
        revoke(data.token);
        setFormError("This account is not an admin. Use the student website to sign in.");
      } else if (data && data.success) {
        localStorage.setItem("authToken", data.token);
        localStorage.setItem("userData", JSON.stringify(data.user));

        showSuccess(`Good to see you, ${data.user?.name || "Admin"}.`, "Signed in");
        onLogin(data);
      } else {
        setFormError(data?.message || "Invalid email or password.");
      }
    } catch (error) {
      console.error("Login error:", error);
      showError(
        "We couldn't reach the server. Check your connection and try again.",
        "Connection problem"
      );
    } finally {
      setIsLoading(false);
    }
  };

  const [resetMode, setResetMode] = useState(false);
  const [resetSent, setResetSent] = useState(false);

  const handleForgotPassword = () => {
    setResetMode(true);
    setResetSent(false);
    setFormError("");
  };

  const handleSendReset = async (e) => {
    e.preventDefault();
    const email = formData.email.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setFormError("Enter the email address of your admin account.");
      return;
    }

    setIsLoading(true);
    setFormError("");
    try {
      const data = await forgotPasswordAPI(email);
      if (data?.status === 200) {
        setResetSent(true);
      } else {
        setFormError(data?.message || "We couldn't send a reset link. Try again in a minute.");
      }
    } catch {
      showError("We couldn't reach the server. Check your connection and try again.", "Connection problem");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="login-page">
      <aside className="login-hero" aria-hidden="true">
        <div className="login-hero-orbit orbit-1"></div>
        <div className="login-hero-orbit orbit-2"></div>
        <div className="login-hero-content">
          <div className="login-hero-logo">
            <img src="/logo.png" alt="" />
          </div>
          <h2>Build better tests, faster.</h2>
          <p>
            Create bilingual test series, manage courses and subscriptions, and follow student progress,
            all in one place.
          </p>
          <ul className="login-hero-points">
            <li>
              <i className="bi bi-journal-check"></i> Bilingual questions with math & chemistry support
            </li>
            <li>
              <i className="bi bi-bar-chart-line"></i> Leaderboards and detailed attempt reviews
            </li>
            <li>
              <i className="bi bi-shield-check"></i> Secure admin access
            </li>
          </ul>
        </div>
      </aside>

      <main className="login-panel">
        <div className="login-card">
          <div className="login-header">
            <img src="/logo.png" alt="Jupiter Institute" className="login-logo" />
            <h1>Welcome back</h1>
            <p>Sign in to the Jupiter Institute admin console</p>
          </div>

          {formError && (
            <div className="alert alert-danger login-alert" role="alert">
              <i className="bi bi-exclamation-circle"></i>
              <div className="alert-body">{formError}</div>
            </div>
          )}

          {resetMode ? (
            <form onSubmit={handleSendReset} className="login-form" noValidate>
              {resetSent ? (
                <div className="alert alert-success login-alert" role="status">
                  <i className="bi bi-envelope-check"></i>
                  <div className="alert-body">
                    If <strong>{formData.email.trim()}</strong> belongs to an account, a reset link is on its way.
                    The link works for 60 minutes.
                  </div>
                </div>
              ) : (
                <>
                  <p className="login-reset-intro">
                    Enter your admin email and we'll send you a link to choose a new password.
                  </p>
                  <div className="form-group">
                    <label htmlFor="reset-email">Email address</label>
                    <div className="input-icon">
                      <i className="bi bi-envelope"></i>
                      <input
                        type="email"
                        id="reset-email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="admin@jupiterinstitution.com"
                        autoComplete="username"
                        autoFocus
                        disabled={isLoading}
                      />
                    </div>
                  </div>
                  <button type="submit" className="btn-primary btn-lg btn-block" disabled={isLoading}>
                    {isLoading ? (
                      <>
                        <span className="btn-spinner"></span> Sending…
                      </>
                    ) : (
                      <>
                        Send reset link <i className="bi bi-send"></i>
                      </>
                    )}
                  </button>
                </>
              )}
              <button
                type="button"
                className="login-link login-back"
                onClick={() => {
                  setResetMode(false);
                  setFormError("");
                }}
              >
                <i className="bi bi-arrow-left"></i> Back to sign in
              </button>
            </form>
          ) : (
          <form onSubmit={handleSubmit} className="login-form" noValidate>
            <div className="form-group">
              <label htmlFor="email">Email address</label>
              <div className="input-icon">
                <i className="bi bi-envelope"></i>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="admin@jupiterinstitution.com"
                  autoComplete="username"
                  autoFocus
                  disabled={isLoading}
                  className={formError && !formData.email ? "error" : ""}
                />
              </div>
            </div>

            <div className="form-group">
              <div className="login-label-row">
                <label htmlFor="password">Password</label>
                <button type="button" onClick={handleForgotPassword} className="login-link">
                  Forgot password?
                </button>
              </div>
              <div className="input-icon password-field">
                <i className="bi bi-lock"></i>
                <input
                  type={showPassword ? "text" : "password"}
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="Enter your password"
                  autoComplete="current-password"
                  disabled={isLoading}
                  className={formError && !formData.password ? "error" : ""}
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  tabIndex={-1}
                >
                  <i className={`bi ${showPassword ? "bi-eye-slash" : "bi-eye"}`}></i>
                </button>
              </div>
            </div>

            <button type="submit" className="btn-primary btn-lg btn-block" disabled={isLoading}>
              {isLoading ? (
                <>
                  <span className="btn-spinner"></span> Signing in…
                </>
              ) : (
                <>
                  Sign in <i className="bi bi-arrow-right"></i>
                </>
              )}
            </button>
          </form>
          )}

          <p className="login-footer">© {new Date().getFullYear()} Jupiter Institute</p>
        </div>
      </main>
    </div>
  );
}

export default Login;
